import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Star,
  CheckCircle,
  XCircle,
  Loader2,
  Crown,
  Users,
  DollarSign
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMembershipManagement = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    plan_id: '',
    name: '',
    description: '',
    price: 0,
    original_price: 0,
    discount_percentage: 0,
    billing_cycle: 'monthly',
    features: '',
    is_recommended: false,
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    // Check admin auth
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    fetchPlans();
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/membership/plans`);
      const data = await response.json();
      
      if (data.success) {
        setPlans(data.plans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      toast({
        title: 'Error',
        description: 'Failed to load membership plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setSelectedPlan(null);
    setFormData({
      plan_id: '',
      name: '',
      description: '',
      price: 0,
      original_price: 0,
      discount_percentage: 0,
      billing_cycle: 'monthly',
      features: '',
      is_recommended: false,
      is_active: true,
      display_order: plans.length + 1
    });
    setShowModal(true);
  };

  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      plan_id: plan.plan_id,
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      original_price: plan.original_price || plan.price,
      discount_percentage: plan.discount_percentage || 0,
      billing_cycle: plan.billing_cycle || 'monthly',
      features: Array.isArray(plan.features) ? plan.features.join('\n') : plan.features,
      is_recommended: plan.is_recommended || false,
      is_active: plan.is_active !== false,
      display_order: plan.display_order || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formPayload = new FormData();
      formPayload.append('plan_id', formData.plan_id);
      formPayload.append('name', formData.name);
      formPayload.append('description', formData.description);
      formPayload.append('price', formData.price.toString());
      formPayload.append('original_price', formData.original_price.toString());
      formPayload.append('discount_percentage', formData.discount_percentage.toString());
      formPayload.append('billing_cycle', formData.billing_cycle);
      
      // Convert features to JSON array
      const featuresArray = formData.features.split('\n').filter(f => f.trim());
      formPayload.append('features', JSON.stringify(featuresArray));
      
      formPayload.append('is_recommended', formData.is_recommended.toString());
      formPayload.append('is_active', formData.is_active.toString());
      formPayload.append('display_order', formData.display_order.toString());

      let url, method;
      if (selectedPlan) {
        url = `${API_URL}/api/admin/membership/plans/${selectedPlan.plan_id}`;
        method = 'PUT';
      } else {
        url = `${API_URL}/api/admin/membership/plans/create`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        body: formPayload
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: selectedPlan ? 'Plan updated successfully' : 'Plan created successfully'
        });
        setShowModal(false);
        fetchPlans();
      } else {
        throw new Error(data.detail || 'Failed to save plan');
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;

    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/membership/plans/${selectedPlan.plan_id}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Plan deleted successfully'
        });
        setShowDeleteModal(false);
        setSelectedPlan(null);
        fetchPlans();
      } else {
        throw new Error(data.detail || 'Failed to delete plan');
      }
    } catch (err) {
      console.error('Error deleting plan:', err);
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const togglePlanStatus = async (plan) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/membership/plans/${plan.plan_id}/toggle-active`,
        { method: 'POST' }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: `Plan ${data.is_active ? 'activated' : 'deactivated'} successfully`
        });
        fetchPlans();
      }
    } catch (err) {
      console.error('Error toggling plan status:', err);
      toast({
        title: 'Error',
        description: 'Failed to update plan status',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Membership Plans</h1>
                <p className="text-gray-600 text-sm">Manage your platform membership plans</p>
              </div>
            </div>
            <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add New Plan
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Crown className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Plans</p>
                <p className="text-2xl font-bold">{plans.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Plans</p>
                <p className="text-2xl font-bold">{plans.filter(p => p.is_active).length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Highest Price</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(Math.max(...plans.map(p => p.price)))}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.plan_id} className={`relative ${!plan.is_active ? 'opacity-60' : ''}`}>
              {plan.is_recommended && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 text-xs font-semibold rounded-bl-lg">
                  <Star className="h-3 w-3 inline mr-1" />
                  Recommended
                </div>
              )}
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {plan.description && (
                  <p className="text-sm text-gray-500">{plan.description}</p>
                )}
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    {plan.original_price > plan.price && (
                      <span className="text-gray-400 line-through">
                        {formatCurrency(plan.original_price)}
                      </span>
                    )}
                    <span className="text-3xl font-bold">
                      {plan.price === 0 ? 'Free' : formatCurrency(plan.price)}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-500 text-sm">/{plan.billing_cycle}</span>
                    )}
                  </div>
                  {plan.discount_percentage > 0 && (
                    <Badge variant="outline" className="mt-2 text-green-600 border-green-600">
                      {plan.discount_percentage}% OFF
                    </Badge>
                  )}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-2">Features ({plan.features?.length || 0}):</p>
                  <ul className="space-y-1 text-sm text-gray-700 max-h-32 overflow-y-auto">
                    {plan.features?.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.features?.length > 4 && (
                      <li className="text-blue-600">+{plan.features.length - 4} more...</li>
                    )}
                  </ul>
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditModal(plan)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant={plan.is_active ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => togglePlanStatus(plan)}
                  >
                    {plan.is_active ? (
                      <XCircle className="h-4 w-4" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => {
                      setSelectedPlan(plan);
                      setShowDeleteModal(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {plans.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Crown className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Plans Yet</h3>
              <p className="text-gray-500 mb-4">Create your first membership plan to get started</p>
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                Create Plan
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
            <DialogDescription>
              {selectedPlan ? 'Update the membership plan details' : 'Fill in the details for the new membership plan'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plan_id">Plan ID *</Label>
                <Input
                  id="plan_id"
                  value={formData.plan_id}
                  onChange={(e) => setFormData({...formData, plan_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="e.g., premium"
                  disabled={!!selectedPlan}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Premium"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this plan"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (Rs) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="original_price">Original Price (Rs)</Label>
                <Input
                  id="original_price"
                  type="number"
                  min="0"
                  value={formData.original_price}
                  onChange={(e) => setFormData({...formData, original_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount">Discount %</Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({...formData, discount_percentage: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="billing_cycle">Billing Cycle</Label>
                <Select
                  value={formData.billing_cycle}
                  onValueChange={(value) => setFormData({...formData, billing_cycle: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select cycle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  min="0"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="features">Features (one per line) *</Label>
              <Textarea
                id="features"
                value={formData.features}
                onChange={(e) => setFormData({...formData, features: e.target.value})}
                placeholder="Post up to 10 ads per month&#10;Enhanced visibility&#10;Priority support"
                rows={5}
                required
              />
              <p className="text-xs text-gray-500">Enter each feature on a new line</p>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_recommended"
                  checked={formData.is_recommended}
                  onCheckedChange={(checked) => setFormData({...formData, is_recommended: checked})}
                />
                <Label htmlFor="is_recommended">Mark as Recommended</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  selectedPlan ? 'Update Plan' : 'Create Plan'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the "{selectedPlan?.name}" plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Plan'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMembershipManagement;
